import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Drop from 'LuxKit/Drop'
import type { ImageMediaSource } from 'components/Common/Image/Image'
import { breakpointSizes, mediaQueryUp } from 'components/utils/breakpoint'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LOYALTY_CREDIT_CARD_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { LOYALTY_CREDIT_CARD_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { useLoyaltyCreditCardFeatureFlag } from 'loyalty/hooks/useLoyaltyCreditCardFeatureFlag'
import useLoyaltyCreditCardPlacementTracking from 'loyalty/hooks/useLoyaltyCreditCardPlacementTracking'
import { rem } from 'polished'
import variables from 'styles/vendor/bootstrap/variables'
import * as React from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

/* The desktop Drop exceeds the banner height; clipping enlarges the artwork without deepening the banner. */
const DESKTOP_HEIGHT = 178
const DESKTOP_IMAGE_DIAMETER = 294
const DESKTOP_IMAGE_INSET_START = 48
/* Top alignment keeps the card high while the lower sand extends beyond the clipped banner. */
const DESKTOP_IMAGE_INSET_TOP = -14
const DESKTOP_CONTENT_GAP = 68
const MOBILE_IMAGE_DIAMETER = 106
const MOBILE_IMAGE_INSET_END = 20
/* Reserve inline space for the out-of-flow Drop below desktop. */
const MOBILE_CONTENT_INSET_END = MOBILE_IMAGE_DIAMETER + 16
const TABLET_IMAGE_DIAMETER = 160

/* Mobile and tablet reveal the complete Drop, so use the self-contained crop without padded edges. */
const BANNER_IMAGE_ID = 'societe-credit-card-banner-drop-mobile'

/* Desktop uses a tighter crop for legibility. Its sand-colour lower padding remains outside the
   banner's clipped area; mobile and tablet use `BANNER_IMAGE_ID` instead. */
const BANNER_IMAGE_SOURCES: Array<ImageMediaSource> = [
  {
    media: `(min-width: ${breakpointSizes.desktop.min}px)`,
    id: 'societe-credit-card-banner-drop-v2',
    width: DESKTOP_IMAGE_DIAMETER,
    dpr: 3,
  },
]

const Banner = styled(ClickableLink)`
  position: relative;
  display: block;
  overflow: hidden;
  background-color: ${props => props.theme.palette.highlight.primary.darkBackground};
  color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(28)} ${rem(20)};
  /* Cancel LayoutContainer gutters for the full-bleed mobile banner. */
  margin-inline: -${rem(variables.gutterWidth / 2)};

  ${mediaQueryUp.tablet} {
    margin-inline: 0;
    border-radius: ${props => props.theme.borderRadius.XL};
  }

  ${mediaQueryUp.desktop} {
    display: flex;
    align-items: center;
    gap: ${rem(DESKTOP_CONTENT_GAP)};
    height: ${rem(DESKTOP_HEIGHT)};
    padding-block: 0;
    padding-inline: 0 ${rem(40)};
  }
`

/* Keep the decorative Drop out of flow and reserve its space in Content. Override width directly
   because Drop's inline `--drop-size` cannot vary by breakpoint. */
const BannerDrop = styled(Drop)`
  position: absolute;
  top: 50%;
  margin-block-start: -${rem(MOBILE_IMAGE_DIAMETER / 2)};
  inset-inline-end: ${rem(MOBILE_IMAGE_INSET_END)};
  width: ${rem(MOBILE_IMAGE_DIAMETER)};

  ${mediaQueryUp.tablet} {
    margin-block-start: -${rem(TABLET_IMAGE_DIAMETER / 2)};
    width: ${rem(TABLET_IMAGE_DIAMETER)};
  }

  ${mediaQueryUp.desktop} {
    position: static;
    top: auto;
    flex: 0 0 auto;
    align-self: flex-start;
    width: ${rem(DESKTOP_IMAGE_DIAMETER)};
    margin-block-start: ${rem(DESKTOP_IMAGE_INSET_TOP)};
    margin-inline-start: ${rem(DESKTOP_IMAGE_INSET_START)};
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  padding-inline-end: ${rem(MOBILE_CONTENT_INSET_END)};

  ${mediaQueryUp.tablet} {
    max-width: none;
    padding-inline-end: ${rem(TABLET_IMAGE_DIAMETER + 16)};
  }

  ${mediaQueryUp.desktop} {
    flex: 1 1 auto;
    padding-inline-end: 0;
  }
`

const Eyebrow = styled(BodyText)`
  color: ${props => props.theme.palette.highlight.primary.normalForeground};
`

/* CSS-only visibility avoids an SSR hydration branch on viewport width. */
const BenefitList = styled.ul`
  display: none;
  flex-direction: column;
  gap: ${rem(4)};
  margin: ${rem(6)} 0 0;
  padding: 0;
  list-style: none;

  ${mediaQueryUp.desktop} {
    display: flex;
  }
`

const Benefit = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

/* The visual CTA is non-interactive because Banner is the only link and tab stop. */
const CtaPill = styled.span`
  display: inline-block;
  align-self: flex-start;
  flex: 0 0 auto;
  margin-top: ${rem(16)};
  text-decoration: underline;

  ${mediaQueryUp.desktop} {
    align-self: center;
    margin-top: 0;
    padding: ${rem(10)} ${rem(20)};
    border: 1px solid ${props => props.theme.palette.neutral.default.eight};
    border-radius: ${props => props.theme.borderRadius.round};
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.2s;

    ${mediaHoverable} {
      &:hover {
        background-color: color-mix(in srgb, var(--palette-highlight-primary-dark-background), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
      }
    }
  }
`

function renderSuperscript(chunks: React.ReactNode) {
  return <sup>{chunks}</sup>
}

function LoyaltyCreditCardHomepageBanner() {
  const intl = useIntl()
  const { isEnabled } = useLoyaltyCreditCardFeatureFlag()
  const { impressionRef, onPlacementClick } = useLoyaltyCreditCardPlacementTracking(LOYALTY_CREDIT_CARD_PLACEMENTS.homepageBanner)

  const eyebrowText = intl.formatMessage({
    id: 'n3v_HMXP4bpV6dt33T4N',
    defaultMessage: 'Société Credit Card',
    description: 'Small label above the headline on the Société Credit Card promotional banner on the homepage',
  })

  const headingText = intl.formatMessage({
    id: 'pk5WF3BaBPZyb42gpE3U',
    defaultMessage: '<em>Points</em> that take you places',
    description: 'Headline on the Société Credit Card promotional banner on the homepage, with the first word emphasised',
  }, {
    em: (chunks: React.ReactNode) => <em key="points">{chunks}</em>,
  })

  const ctaText = intl.formatMessage({
    id: 'fZPxSUxfsABI_El1eYPo',
    defaultMessage: 'Find out more',
    description: 'Call to action on the Société Credit Card promotional banner on the homepage, linking to the card landing page',
  })

  const benefits = useMemo(() => [
    {
      key: 'points-earn-rate',
      content: intl.formatMessage({
        id: 'hMADVMxmHQ_idfoyZFZ_',
        defaultMessage: 'Earn 2 Société Points per $1 spent<sup>1</sup>',
        description: 'First benefit line on the Société Credit Card homepage banner, describing the points earn rate with footnote marker one',
      }, {
        sup: renderSuperscript,
      }),
    },
    {
      key: 'luxury-escapes-credit',
      content: intl.formatMessage({
        id: '3xdaWsSSvhZmOSYcvMmq',
        defaultMessage: '$150 Luxury Escapes credit<sup>2</sup>',
        description: 'Second benefit line on the Société Credit Card homepage banner, describing the sign-up credit with footnote marker two',
      }, {
        sup: renderSuperscript,
      }),
    },
    {
      key: 'maldives-prize-draw',
      content: intl.formatMessage({
        id: '7EOjLnytbPrZd9SOeM7w',
        defaultMessage: 'Your chance to WIN a trip to the Maldives<sup>3</sup>',
        description: 'Third benefit line on the Société Credit Card homepage banner, describing the prize draw with footnote marker three',
      }, {
        sup: renderSuperscript,
      }),
    },
  ], [intl])

  const accessibleLabel = intl.formatMessage({
    id: 'UEEFykUyILytAF2huY7s',
    defaultMessage: 'Société Credit Card — points that take you places. Find out more.',
    description: 'Accessible name for the Société Credit Card promotional banner link on the homepage',
  })

  if (!isEnabled) {
    return null
  }

  return <Banner ref={impressionRef} to={`/${LOYALTY_CREDIT_CARD_PAGE}`} aria-label={accessibleLabel} onClick={onPlacementClick}>
    <BannerDrop
      id={BANNER_IMAGE_ID}
      sources={BANNER_IMAGE_SOURCES}
      position="bottom-right"
      size={DESKTOP_IMAGE_DIAMETER}
      alt=""
    />
    <Content>
      <Eyebrow variant="medium">{eyebrowText}</Eyebrow>
      <Heading variant="heading3" colour="dark-contrast">{headingText}</Heading>
      <BenefitList>
        {benefits.map(benefit => <Benefit key={benefit.key}>
          <LineCheckIcon size={16} />
          <BodyText variant="medium">{benefit.content}</BodyText>
        </Benefit>)}
      </BenefitList>
    </Content>
    <CtaPill>
      <BodyText variant="medium" as="span">{ctaText}</BodyText>
    </CtaPill>
  </Banner>
}

export default LoyaltyCreditCardHomepageBanner
